/**
 * Storefront API checkout (cart create) flow.
 *
 * IMPORTANT: All checkout URLs are forced through the permanent Shopify domain
 * (`alice-jewelry-il.myshopify.com`) and tagged with `channel=online_store` so
 * checkout never lands on a custom-domain page that points back at this storefront.
 */
import { PERMANENT_SHOPIFY_DOMAIN, shopifyRequest } from "./client";
import type { LineItemAttribute } from "./types";

export async function createStorefrontCheckout(
  lineItems: Array<{
    variantId: string;
    quantity: number;
    attributes?: LineItemAttribute[];
  }>,
  locale?: string,
  discountCode?: string
) {
  // Build cart input - include discount codes if provided
  const cartInput: {
    lines: Array<{
      merchandiseId: string;
      quantity: number;
      attributes: LineItemAttribute[];
    }>;
    discountCodes?: string[];
  } = {
    lines: lineItems.map((item) => ({
      merchandiseId: item.variantId,
      quantity: item.quantity,
      attributes: item.attributes || [],
    })),
  };

  if (discountCode) {
    cartInput.discountCodes = [discountCode];
  }

  const mutation = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
        }
        userErrors {
          code
          field
          message
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(mutation, { input: cartInput });

    if (
      result.data.cartCreate.userErrors &&
      result.data.cartCreate.userErrors.length > 0
    ) {
      throw new Error(result.data.cartCreate.userErrors[0].message);
    }

    const cart = result.data.cartCreate.cart;

    // Add channel=online_store parameter to avoid password-protected checkout
    const checkoutUrl = new URL(cart.checkoutUrl);

    // IMPORTANT: Shopify may return the shop's primary (custom) domain in checkoutUrl.
    // If that domain is pointed to this Lovable storefront, checkout will break.
    // Force the permanent Shopify domain to ensure checkout always resolves.
    const isNonShopifyHost =
      !checkoutUrl.hostname.endsWith(".myshopify.com") &&
      !checkoutUrl.hostname.endsWith("shopify.com");
    if (isNonShopifyHost) {
      checkoutUrl.hostname = PERMANENT_SHOPIFY_DOMAIN;
    }

    checkoutUrl.searchParams.set("channel", "online_store");

    // Add locale parameter for multi-language support.
    // Only pass Shopify-supported locale codes (2-letter ISO 639-1).
    if (locale) {
      const shortLocale = locale.split("-")[0]; // e.g. 'he-IL' -> 'he'
      checkoutUrl.searchParams.set("locale", shortLocale);
    }

    return {
      id: cart.id,
      webUrl: checkoutUrl.toString(),
    };
  } catch (error) {
    console.error("Error creating checkout:", error);
    throw error;
  }
}
